import React, { useContext } from 'react';
import Section from '../common/Section';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { DataContext } from '../../context/DataProvider';
import { Link } from 'react-router-dom';

const PortfolioPreview = () => {
    const { projects } = useContext(DataContext);
    
    // Sort by display order or creation date if needed. 
    // Assuming backend or provider already sorts them, or we sort here.
    // Let's just take the first 3.
    const displayProjects = projects.slice(0, 3);

    if (!displayProjects || displayProjects.length === 0) {
        return null; // Or return an empty Fragment if we don't want to hide the whole section
    }

    return (
        <Section className="bg-white relative overflow-hidden">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#bebd19]/5 rounded-full blur-[120px] pointer-events-none" />

            <div className="container mx-auto px-4 relative z-10">
                <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
                    <div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">Check Out Our Work</h2>
                        <p className="text-gray-500 text-lg max-w-xl">
                            We take pride in delivering exceptional digital experiences. Here's a glimpse of what we've built.
                        </p>
                    </div>
                    <Link 
                        to="/projects" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-[#bebd19] font-bold hover:gap-4 transition-all"
                    >
                        View All Projects <ArrowRight size={20} />
                    </Link>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {displayProjects.map((project) => (
                        <div key={project.id} className="group relative rounded-2xl overflow-hidden bg-gray-100 border border-gray-200 hover:border-[#bebd19] transition-all duration-500">
                            {/* Image Container */}
                            <div className="h-64 overflow-hidden relative">
                                <div className="absolute inset-0 bg-black/5 group-hover:bg-black/0 transition-colors z-10" />
                                <img
                                    src={project.image_url || 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=800'}
                                    alt={project.title}
                                    className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                                />
                            </div>

                            {/* Content */}
                            <div className="p-6">
                                <span className="text-xs font-bold text-[#bebd19] uppercase tracking-wider mb-2 block">
                                    {project.category}
                                </span>
                                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#bebd19] transition-colors">
                                    {project.title}
                                </h3>
                                <p className="text-gray-500 text-sm mb-6 line-clamp-2">
                                    {project.description}
                                </p>
                                {project.project_link && (
                                    <a 
                                        href={project.project_link} 
                                        target="_blank" 
                                        rel="noopener noreferrer" 
                                        className="flex items-center gap-2 text-gray-900 text-sm font-semibold hover:text-[#bebd19] transition-colors"
                                    >
                                        View Project <ExternalLink size={14} />
                                    </a>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </Section>
    );
};

export default PortfolioPreview;
